import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { flattenPages } from '../api/hooks/useDiscovery';
import {
  useMarkNotificationsRead,
  useNotifications,
  useUnreadCount,
  type NotificationItem,
} from '../api/hooks/useNotifications';
import { formatRelativeTime } from '../lib/format';
import { Avatar, Spinner } from './ui';

export const NotificationsBell = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement | null>(null);
  const unread = useUnreadCount(true);
  // The list is only fetched once the panel is opened -- a badge does not need it.
  const notifications = useNotifications(open);
  const markRead = useMarkNotificationsRead();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (container.current !== null && !container.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const items = flattenPages<NotificationItem>(notifications.data);
  const unreadIds = items.filter((item) => item.readAt === null).map((item) => item.id);
  const count = unread.data?.count ?? 0;

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        aria-expanded={open}
        className="relative rounded-full p-2 text-ink-200 hover:bg-ink-700"
      >
        <span aria-hidden="true">&#9737;</span>
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-[11px] font-semibold leading-[18px] text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 max-h-[70vh] w-80 overflow-y-auto rounded-xl border border-ink-600 bg-ink-800 shadow-xl">
          <div className="flex items-center justify-between border-b border-ink-700 px-4 py-2">
            <p className="text-sm font-medium text-ink-100">Notifications</p>
            {unreadIds.length > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate(unreadIds)}
                className="text-xs text-brand-400 hover:text-brand-500"
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.isLoading && <Spinner label="Loading" />}
          {!notifications.isLoading && items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-ink-400">Nothing new.</p>
          )}

          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <Link
                  to={item.videoId !== null ? `/watch/${item.videoId}` : '/'}
                  onClick={() => {
                    setOpen(false);
                    if (item.readAt === null) markRead.mutate([item.id]);
                  }}
                  className={`flex gap-3 px-4 py-3 hover:bg-ink-700 ${item.readAt === null ? 'bg-ink-700/40' : ''}`}
                >
                  <Avatar
                    src={item.channel?.avatarUrl ?? null}
                    name={item.channel?.displayName ?? 'Channel'}
                    size={32}
                  />
                  <div className="min-w-0">
                    <p className="text-sm text-ink-100">
                      <span className="font-medium">{item.channel?.displayName ?? 'A channel'}</span> uploaded{' '}
                      <span className="text-ink-200">{item.video?.title ?? 'a new video'}</span>
                    </p>
                    <p className="text-xs text-ink-400">{formatRelativeTime(item.createdAt)}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {notifications.hasNextPage === true && (
            <button
              type="button"
              onClick={() => void notifications.fetchNextPage()}
              className="w-full py-3 text-center text-sm text-brand-400 hover:bg-ink-700"
            >
              Load more
            </button>
          )}
        </div>
      )}
    </div>
  );
};
